import React, { useEffect, useState } from "react";
import {
  AlertTriangle,
  BadgeCheck,
  Bot,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  FileText,
  Image as ImageIcon,
  Loader2,
  Paperclip,
  RefreshCw,
  Sparkles,
  Tag,
  Wand2,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { CATEGORY_STYLES, absTime, inr, initials, prettyBytes, relTime } from "@/lib/format";
import { attachmentUrl, classifyThread, errMessage, getThread, refreshThread } from "@/lib/api";
import { toast } from "sonner";

const Message = ({ m, defaultOpen }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div
      className={cn(
        "rounded-lg border bg-card",
        m.is_approval_message ? "border-primary" : "border-border"
      )}
      data-testid="preview-message"
    >
      <button
        type="button"
        className="flex w-full items-start gap-2.5 rounded-lg p-3 text-left transition-colors hover:bg-secondary/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-secondary text-[10px] font-semibold text-secondary-foreground">
          {initials(m.from_name || m.from_email)}
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-center gap-1.5">
            <span className="truncate text-xs font-semibold">{m.from_name || m.from_email}</span>
            {m.is_from_approver ? (
              <span className="inline-flex items-center gap-1 rounded-full border border-success-border bg-success-surface px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-[0.08em] text-success-text">
                <BadgeCheck className="h-2.5 w-2.5" aria-hidden="true" /> Approver
              </span>
            ) : null}
            {m.is_approval_message ? (
              <span className="rounded-full border border-primary bg-card px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-[0.08em] text-primary">
                Approval used
              </span>
            ) : null}
          </span>
          <span className="vde-code block truncate text-[10px] text-muted-foreground">
            {m.from_email} · {absTime(m.date)}
          </span>
          {!open ? (
            <span className="mt-1 line-clamp-2 block text-[11px] text-muted-foreground">
              {m.snippet || (m.body_text || "").slice(0, 200)}
            </span>
          ) : null}
        </span>
        {open ? (
          <ChevronUp className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        ) : (
          <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        )}
      </button>

      {open ? (
        <div className="border-t border-border px-3 py-2.5">
          <pre className="vde-scrollbar-brand max-h-[320px] overflow-auto overscroll-contain whitespace-pre-wrap break-words font-sans text-[11.5px] leading-relaxed text-foreground">
            {m.body_text || "(no text body)"}
          </pre>
          {(m.attachments || []).length ? (
            <div className="mt-2.5 space-y-1.5">
              {m.attachments.map((a) => (
                <a
                  key={a.attachment_id}
                  href={attachmentUrl(a.url)}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-2 rounded-md border border-border bg-secondary/50 px-2 py-1.5 transition-colors hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  data-testid="preview-attachment"
                >
                  {/\.pdf$/i.test(a.filename) ? (
                    <FileText className="h-3.5 w-3.5 shrink-0 text-danger-text" aria-hidden="true" />
                  ) : (
                    <ImageIcon className="h-3.5 w-3.5 shrink-0 text-info-text" aria-hidden="true" />
                  )}
                  <span className="min-w-0 flex-1 truncate text-[11px] font-medium">{a.filename}</span>
                  <span className="vde-code shrink-0 text-[10px] text-muted-foreground">
                    {prettyBytes(a.size)}
                  </span>
                  <ExternalLink className="h-3 w-3 shrink-0 text-muted-foreground" />
                </a>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default function ThreadPreview({ threadId, onClose, onExtract, extracting, onThreadUpdated, canEdit = true }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState(null);

  const load = async (id) => {
    setLoading(true);
    setError(null);
    try {
      setData(await getThread(id));
    } catch (e) {
      setError(errMessage(e, "Could not load this thread"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (threadId) load(threadId);
    else setData(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId]);

  const t = data?.thread;
  const cat = CATEGORY_STYLES[t?.category] || CATEGORY_STYLES.Other;

  const reclassify = async () => {
    setBusy("classify");
    try {
      const updated = await classifyThread(threadId);
      toast.success("Re-classified", { description: `${updated.category}` });
      onThreadUpdated?.(updated);
      load(threadId);
    } catch (e) {
      toast.error("Classification failed", { description: errMessage(e) });
    } finally {
      setBusy("");
    }
  };

  const resync = async () => {
    setBusy("refresh");
    try {
      const updated = await refreshThread(threadId);
      toast.success("Thread refreshed from Gmail");
      onThreadUpdated?.(updated);
      load(threadId);
    } catch (e) {
      toast.error("Refresh failed", { description: errMessage(e) });
    } finally {
      setBusy("");
    }
  };

  return (
    /* h-full + min-h-0 lets the body below the pinned header own the scrolling. */
    <div
      className="vde-card flex h-full min-h-0 flex-col overflow-hidden xl:flex-1"
      data-testid="thread-preview"
    >
      {/* pinned preview header — never scrolls */}
      <div className="flex shrink-0 items-start gap-2 border-b border-border bg-card px-4 py-3">
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold" data-testid="preview-subject">
            {t?.subject || (loading ? "Loading…" : "Select a mail")}
          </p>
          {t ? (
            <p className="mt-0.5 truncate text-[11px] text-muted-foreground">
              {t.from_name ? `${t.from_name} · ` : ""}
              {t.from_email} · {t.message_count} message{t.message_count === 1 ? "" : "s"} ·{" "}
              {relTime(t.last_message_at)}
            </p>
          ) : null}
        </div>
        {onClose ? (
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 shrink-0"
            onClick={onClose}
            aria-label="Close preview"
            data-testid="preview-close-button"
          >
            <X className="h-4 w-4" />
          </Button>
        ) : null}
      </div>

      {loading ? (
        <div className="flex min-h-0 flex-1 items-center justify-center gap-2 p-6 text-xs text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Loading thread…
        </div>
      ) : null}

      {error ? (
        <div className="m-4 shrink-0 rounded-lg border border-danger-border bg-danger-surface px-3 py-2 text-xs text-danger-text">
          {error}
        </div>
      ) : null}

      {!threadId && !loading ? (
        <div className="flex min-h-0 flex-1 flex-col items-center justify-center p-8 text-center">
          <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-secondary">
            <Sparkles className="h-4 w-4 text-primary" aria-hidden="true" />
          </div>
          <p className="text-sm font-medium">Nothing selected</p>
          <p className="mt-1 max-w-[260px] text-xs text-muted-foreground">
            Pick a mail on the left to read the full thread, see attachments and extract vendor rows.
          </p>
          <div className="vde-brand-stripe mt-4 w-24 rounded-full" aria-hidden="true" />
        </div>
      ) : null}

      {t && !loading ? (
        /* independent scroll region for the opened mail */
        <div
          className="vde-scrollbar-brand min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-3"
          data-testid="inbox-thread-preview-scroll"
        >
          {/* AI summary card */}
          <div className="rounded-xl border border-border bg-secondary/40 p-3">
            <div className="flex flex-wrap items-center gap-1.5">
              <span
                className={cn(
                  "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold",
                  cat.badge
                )}
                data-testid="preview-category-badge"
              >
                <Bot className="h-2.5 w-2.5" aria-hidden="true" /> {t.category}
              </span>
              {t.category_confidence ? (
                <span className="vde-code text-[10px] text-muted-foreground">
                  {Math.round(t.category_confidence * 100)}% confidence
                </span>
              ) : null}
              {t.is_labeled ? (
                <span className="inline-flex items-center gap-1 rounded-full border border-info-border bg-info-surface px-2 py-0.5 text-[10px] font-semibold text-info-text">
                  <Tag className="h-2.5 w-2.5" aria-hidden="true" /> Labeled
                </span>
              ) : null}
            </div>

            {t.ai_summary ? (
              <p className="mt-2 text-[12px] leading-relaxed" data-testid="preview-ai-summary">
                {t.ai_summary}
              </p>
            ) : null}
            {t.category_reason ? (
              <p className="mt-1 text-[11px] italic text-muted-foreground">{t.category_reason}</p>
            ) : null}

            <dl className="mt-2.5 grid grid-cols-2 gap-x-3 gap-y-2">
              {t.vendor_hint ? (
                <div className="col-span-2">
                  <dt className="text-[9px] uppercase tracking-[0.08em] text-muted-foreground">Vendor</dt>
                  <dd className="truncate text-[11.5px] font-medium">{t.vendor_hint}</dd>
                </div>
              ) : null}
              {t.requested_amount ? (
                <div>
                  <dt className="text-[9px] uppercase tracking-[0.08em] text-muted-foreground">
                    Requested
                  </dt>
                  <dd className="vde-code text-[11.5px]">₹{inr(t.requested_amount)}</dd>
                </div>
              ) : null}
              {t.approved_amount ? (
                <div>
                  <dt className="text-[9px] uppercase tracking-[0.08em] text-muted-foreground">
                    Approved (latest)
                  </dt>
                  <dd
                    className="vde-code text-[11.5px] font-semibold text-success-text"
                    data-testid="preview-approved-amount"
                  >
                    ₹{inr(t.approved_amount)}
                  </dd>
                </div>
              ) : null}
              {t.approver_email ? (
                <div className="col-span-2">
                  <dt className="text-[9px] uppercase tracking-[0.08em] text-muted-foreground">
                    Approved by
                  </dt>
                  <dd className="vde-code truncate text-[11.5px]" data-testid="preview-approver">
                    {t.approver_email}
                    {t.approved_at ? ` · ${absTime(t.approved_at)}` : ""}
                  </dd>
                  {t.approval_source ? (
                    <span
                      className={cn(
                        "mt-1 inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-[0.08em]",
                        t.approval_source === "reply"
                          ? "border-success-border bg-success-surface text-success-text"
                          : "border-warning-border bg-warning-surface text-warning-text"
                      )}
                      title={
                        t.approval_source === "reply"
                          ? "The approver replied directly on this thread"
                          : "Approval found in quoted/forwarded history - the quote below was verified against the mail"
                      }
                      data-testid="preview-approval-source"
                    >
                      {t.approval_source === "reply" ? "approved by reply" : "approval quoted in mail"}
                    </span>
                  ) : null}
                </div>
              ) : null}
            </dl>

            {t.approved_evidence ? (
              <p className="mt-2 rounded-md border-l-2 border-primary bg-card px-2 py-1.5 text-[11px] italic text-foreground">
                “{t.approved_evidence}”
              </p>
            ) : null}

            {t.needs_attention_reason ? (
              <p className="mt-2 flex items-start gap-1.5 rounded-md border border-warning-border bg-warning-surface px-2 py-1.5 text-[11px] text-warning-text">
                <AlertTriangle className="mt-px h-3 w-3 shrink-0" aria-hidden="true" />
                {t.needs_attention_reason}
              </p>
            ) : null}
            {t.classification_error ? (
              <p className="mt-2 rounded-md border border-danger-border bg-danger-surface px-2 py-1.5 text-[11px] text-danger-text">
                Classification error: {t.classification_error}
              </p>
            ) : null}
          </div>

          {/* actions */}
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {canEdit ? (
              <>
                <Button
                  size="sm"
                  className="gap-1.5 active:scale-[0.98]"
                  onClick={() => onExtract?.(t.thread_id)}
                  disabled={extracting}
                  data-testid="preview-extract-button"
                >
                  {extracting ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                  ) : (
                    <Wand2 className="h-3.5 w-3.5" aria-hidden="true" />
                  )}
                  {t.extracted_row_count ? "Re-extract vendors" : "Extract vendors"}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="gap-1.5 bg-card"
                  onClick={reclassify}
                  disabled={busy === "classify"}
                  data-testid="preview-reclassify-button"
                >
                  {busy === "classify" ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                  ) : (
                    <Bot className="h-3.5 w-3.5" aria-hidden="true" />
                  )}
                  Re-classify
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="gap-1.5 bg-card"
                  onClick={resync}
                  disabled={busy === "refresh"}
                  data-testid="preview-refresh-button"
                >
                  {busy === "refresh" ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                  ) : (
                    <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
                  )}
                  Refresh
                </Button>
              </>
            ) : null}
            {t.gmail_link ? (
              <a
                href={t.gmail_link}
                target="_blank"
                rel="noreferrer"
                className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-border bg-card px-2.5 text-[11px] font-medium transition-colors hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                data-testid="preview-gmail-link"
              >
                <ExternalLink className="h-3 w-3" aria-hidden="true" /> Open in Gmail
              </a>
            ) : null}
          </div>

          {data.queue_row_count ? (
            <p className="mt-2 text-[11px] text-muted-foreground" data-testid="preview-queue-count">
              {data.queue_row_count} vendor row{data.queue_row_count === 1 ? "" : "s"} already in the
              queue from this thread.
            </p>
          ) : null}

          {/* messages */}
          <div className="mt-4 space-y-2">
            <p className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
              <Paperclip className="h-3 w-3" aria-hidden="true" /> Thread ({data.messages.length})
            </p>
            {data.messages.map((m, i) => (
              <Message key={m.message_id} m={m} defaultOpen={i === data.messages.length - 1} />
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
